import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { LiveBettingContext } from 'context/liveBettingContext';
import { MatchDetails } from 'services/api/matchDetailsService';
import { LiveCountKey, LiveEvent } from 'types/LiveEvent';
import { liveClock } from 'utils/sportEvent.utils';
import styles from './LivePitch.module.css';

/**
 * A drawn pitch for a football match in play, from the feed we already have:
 * the live scoreboard (every few seconds: goals, corners, cards, throw-ins,
 * free kicks, goal kicks, penalties, substitutions) and the match statistics
 * (about every 45 s: attacks, dangerous attacks, possession). The feed has no
 * ball position, so the ball shows the zone of the latest action, not the
 * exact spot. The home side attacks to the right.
 */

type Side = 'home' | 'away';
type Kind =
  | 'possession'
  | 'attack'
  | 'dangerous'
  | 'goal'
  | 'corner'
  | 'yellow'
  | 'red'
  | 'freekick'
  | 'goalkick'
  | 'throwin'
  | 'penalty'
  | 'substitution';

interface Action {
  kind: Kind;
  side: Side;
  at: number;
}

/** Scoreboard columns in the order one update tells them (most telling first). */
const COUNT_KINDS: [LiveCountKey, Kind][] = [
  ['goals', 'goal'],
  ['penalties', 'penalty'],
  ['red', 'red'],
  ['yellow', 'yellow'],
  ['corners', 'corner'],
  ['freekicks', 'freekick'],
  ['goalkicks', 'goalkick'],
  ['throwins', 'throwin'],
  ['substitutions', 'substitution'],
];

/** An event stays on the pitch this long before the play goes on. */
const EVENT_HOLD_MS: Partial<Record<Kind, number>> = {
  goal: 20_000,
  penalty: 15_000,
};
const DEFAULT_HOLD_MS = 10_000;
/** The ball moves a little within its zone this often. */
const DRIFT_MS = 2_500;

/** A steady pseudo-random 0-1 from a number (no jumping on re-render). */
const rand = (seed: number) => {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
};

/** Where the ball is for an action, on a 100 x 64 pitch; home attacks right. */
const ballSpot = (a: Action, tick: number): { x: number; y: number } => {
  const r1 = rand(a.at + tick * 7.1);
  const r2 = rand(a.at * 1.7 + tick * 3.3);
  let x = 50;
  let y = 32;
  switch (a.kind) {
    case 'possession':
      x = 54 + (r1 - 0.5) * 14;
      y = 32 + (r2 - 0.5) * 34;
      break;
    case 'attack':
      x = 70 + (r1 - 0.5) * 12;
      y = 32 + (r2 - 0.5) * 36;
      break;
    case 'dangerous':
      x = 85 + (r1 - 0.5) * 6;
      y = 32 + (r2 - 0.5) * 22;
      break;
    case 'goal':
      x = 99;
      y = 32;
      break;
    case 'corner':
      x = 99;
      y = rand(a.at) < 0.5 ? 1.2 : 62.8;
      break;
    case 'penalty':
      x = 89;
      y = 32;
      break;
    case 'freekick':
      x = 72 + (rand(a.at) - 0.5) * 10;
      y = 32 + (rand(a.at * 3) - 0.5) * 30;
      break;
    case 'goalkick':
      // From the side's own six-yard box.
      x = 6;
      y = 32 + (rand(a.at) - 0.5) * 8;
      break;
    case 'throwin':
      x = 35 + rand(a.at) * 40;
      y = rand(a.at * 5) < 0.5 ? 0.6 : 63.4;
      break;
    default:
      x = 50 + (r1 - 0.5) * 10;
      y = 32 + (r2 - 0.5) * 20;
  }
  return a.side === 'home' ? { x, y } : { x: 100 - x, y };
};

const num = (v?: string) => {
  const n = Number.parseFloat(v ?? '');
  return Number.isFinite(n) ? n : 0;
};

interface Props {
  details: MatchDetails;
  /** Shown under the pitch: the latest key events. */
  children?: React.ReactNode;
}

export const LivePitch = ({ details, children }: Props) => {
  const { t } = useTranslation();
  const live = useContext(LiveBettingContext);
  const liveEvent: LiveEvent | undefined = (
    live?.events?.get(1) as LiveEvent[] | undefined
  )?.find((e) => String(e.fi) === String(details.fi));

  const stats = details.stats ?? {};
  const [ambient, setAmbient] = useState<Action>(() => ({
    kind: 'possession',
    side:
      num(stats.possession_rt?.[1]) > num(stats.possession_rt?.[0])
        ? 'away'
        : 'home',
    at: Date.now(),
  }));
  const [event, setEvent] = useState<Action | null>(null);
  const [tick, setTick] = useState(0);
  const [now, setNow] = useState(Date.now());

  // The scoreboard: whatever counter went up is the latest action.
  const prevCounts = useRef<LiveEvent['liveCounts'] | null>(null);
  useEffect(() => {
    const counts = liveEvent?.liveCounts;
    if (!counts) return;
    const prev = prevCounts.current;
    prevCounts.current = counts;
    if (!prev) return;
    for (const [key, kind] of COUNT_KINDS) {
      const was = prev[key];
      const is = counts[key];
      if (!was || !is) continue;
      const side: Side | null =
        is[0] > was[0] ? 'home' : is[1] > was[1] ? 'away' : null;
      if (side) {
        setEvent({ kind, side, at: Date.now() });
        return;
      }
    }
  }, [liveEvent?.liveCounts]);

  // A goal can show in the score before the scoreboard column moves.
  const prevScore = useRef<string | null>(null);
  useEffect(() => {
    const score = details.ss ?? null;
    const prev = prevScore.current;
    prevScore.current = score;
    if (!prev || !score || prev === score) return;
    const [h0, a0] = prev.split('-').map(Number);
    const [h1, a1] = score.split('-').map(Number);
    if (h1 > h0) setEvent({ kind: 'goal', side: 'home', at: Date.now() });
    else if (a1 > a0) setEvent({ kind: 'goal', side: 'away', at: Date.now() });
  }, [details.ss]);

  // The statistics: who has been attacking since the last update.
  const prevStats = useRef<MatchDetails['stats'] | null>(null);
  useEffect(() => {
    const cur = details.stats;
    if (!cur) return;
    const prev = prevStats.current;
    prevStats.current = cur;
    const delta = (key: string, i: 0 | 1) =>
      num(cur[key]?.[i]) - (prev ? num(prev[key]?.[i]) : num(cur[key]?.[i]));
    const dh = delta('dangerous_attacks', 0);
    const da = delta('dangerous_attacks', 1);
    const ah = delta('attacks', 0);
    const aa = delta('attacks', 1);
    let next: Action;
    if (dh > 0 || da > 0) {
      next = { kind: 'dangerous', side: dh >= da ? 'home' : 'away', at: 0 };
    } else if (ah > 0 || aa > 0) {
      next = { kind: 'attack', side: ah >= aa ? 'home' : 'away', at: 0 };
    } else {
      const ph = num(cur.possession_rt?.[0]);
      const pa = num(cur.possession_rt?.[1]);
      next = { kind: 'possession', side: pa > ph ? 'away' : 'home', at: 0 };
    }
    next.at = Date.now();
    setAmbient(next);
  }, [details.stats]);

  useEffect(() => {
    const id = window.setInterval(() => {
      setTick((x) => x + 1);
      setNow(Date.now());
    }, DRIFT_MS);
    return () => window.clearInterval(id);
  }, []);

  // At half time nobody plays: the ball waits on the centre spot.
  const pause = !!liveEvent?.halfTime;
  const hold = event ? EVENT_HOLD_MS[event.kind] ?? DEFAULT_HOLD_MS : 0;
  const showEvent = event && now - event.at < hold;
  // Cards and substitutions stop nothing: the ball stays with the play.
  const ballAction =
    showEvent && !['yellow', 'red', 'substitution'].includes(event!.kind)
      ? event!
      : ambient;
  const banner = showEvent ? event! : ambient;
  const ball = pause
    ? { x: 50, y: 32 }
    : ballSpot(ballAction, ballAction === event ? 0 : tick);
  const team = (s: Side) =>
    s === 'home' ? details.home.name : details.away.name;

  const counts = liveEvent?.liveCounts ?? {};
  const pair = (
    countKey: LiveCountKey | null,
    statKey: string
  ): [number, number] | null => {
    const c = countKey ? counts[countKey] : undefined;
    if (c) return c;
    const s = stats[statKey];
    return s ? [num(s[0]), num(s[1])] : null;
  };
  const rows: [string, [number, number] | null, boolean?][] = [
    ['stat_possession_rt', pair(null, 'possession_rt'), true],
    ['stat_dangerous_attacks', pair(null, 'dangerous_attacks')],
    ['stat_on_target', pair(null, 'on_target')],
    ['stat_corners', pair('corners', 'corners')],
    ['stat_yellowcards', pair('yellow', 'yellowcards')],
    ['stat_redcards', pair('red', 'redcards')],
  ];
  const da = pair(null, 'dangerous_attacks');
  const momentum =
    da && da[0] + da[1] > 0 ? (da[0] / (da[0] + da[1])) * 100 : 50;

  const zone =
    !pause && (banner.kind === 'attack' || banner.kind === 'dangerous')
      ? banner.side === 'home'
        ? {
            x: banner.kind === 'dangerous' ? 75 : 58,
            w: banner.kind === 'dangerous' ? 25 : 42,
          }
        : { x: 0, w: banner.kind === 'dangerous' ? 25 : 42 }
      : null;

  return (
    <div className={styles.wrapper}>
      <div className={styles.topLine}>
        <span className={styles.homeName}>{details.home.name}</span>
        <span className={styles.clock}>
          {liveEvent ? liveClock(liveEvent, t) : ''}{' '}
          <strong>{(details.ss ?? '').replace('-', ' : ')}</strong>
        </span>
        <span className={styles.awayName}>{details.away.name}</span>
      </div>
      <div className={styles.pitchBox}>
        <svg
          className={styles.pitch}
          viewBox="0 0 100 64"
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={t('pitch_label')}
        >
          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
            <rect
              key={i}
              x={i * 10}
              y={0}
              width={10}
              height={64}
              className={i % 2 ? styles.stripeDark : styles.stripeLight}
            />
          ))}
          {zone && (
            <rect
              key={`${banner.kind}-${banner.side}-${banner.at}`}
              x={zone.x}
              y={0}
              width={zone.w}
              height={64}
              className={classNames(
                styles.zone,
                banner.side === 'home' ? styles.zoneHome : styles.zoneAway
              )}
            />
          )}
          <g className={styles.lines}>
            <rect x={0.5} y={0.5} width={99} height={63} />
            <line x1={50} y1={0.5} x2={50} y2={63.5} />
            <circle cx={50} cy={32} r={8.5} />
            <circle cx={50} cy={32} r={0.6} className={styles.spot} />
            <rect x={0.5} y={14} width={15.5} height={36} />
            <rect x={0.5} y={24.5} width={5.5} height={15} />
            <rect x={84} y={14} width={15.5} height={36} />
            <rect x={94} y={24.5} width={5.5} height={15} />
            <circle cx={11} cy={32} r={0.5} className={styles.spot} />
            <circle cx={89} cy={32} r={0.5} className={styles.spot} />
            <path d="M16 25.5 A9 9 0 0 1 16 38.5" />
            <path d="M84 25.5 A9 9 0 0 0 84 38.5" />
          </g>
          <rect
            x={-0.6}
            y={28}
            width={1.1}
            height={8}
            className={styles.goal}
          />
          <rect
            x={99.5}
            y={28}
            width={1.1}
            height={8}
            className={styles.goal}
          />
          <g
            className={styles.ball}
            style={{ transform: `translate(${ball.x}px, ${ball.y}px)` }}
          >
            <circle r={2.6} className={styles.ballHalo} />
            <circle r={1.15} className={styles.ballDot} />
          </g>
        </svg>
        {pause ? (
          <div className={styles.banner}>
            <span className={styles.bannerKind}>{t('live_half_time')}</span>
          </div>
        ) : (
          <div
            key={`${banner.kind}-${banner.at}`}
            className={classNames(
              styles.banner,
              banner.side === 'home' ? styles.bannerHome : styles.bannerAway,
              showEvent && styles.bannerEvent,
              banner.kind === 'goal' && showEvent && styles.bannerGoal
            )}
          >
            <span className={styles.bannerKind}>
              {banner.kind === 'yellow' && <i className={styles.cardYellow} />}
              {banner.kind === 'red' && <i className={styles.cardRed} />}
              {t(`pitch_${banner.kind}`)}
            </span>
            <span className={styles.bannerTeam}>{team(banner.side)}</span>
          </div>
        )}
      </div>

      <div className={styles.momentum}>
        <span>{t('pitch_momentum')}</span>
        <div className={styles.momentumBar}>
          <i className={styles.momHome} style={{ width: `${momentum}%` }} />
          <i
            className={styles.momAway}
            style={{ width: `${100 - momentum}%` }}
          />
        </div>
      </div>

      <div className={styles.stats}>
        {rows.map(([label, p, pct]) =>
          p ? (
            <div key={label} className={styles.statRow}>
              <strong className={classNames({ [styles.lead]: p[0] > p[1] })}>
                {p[0]}
                {pct ? '%' : ''}
              </strong>
              <span>{t(label)}</span>
              <strong className={classNames({ [styles.lead]: p[1] > p[0] })}>
                {p[1]}
                {pct ? '%' : ''}
              </strong>
            </div>
          ) : null
        )}
      </div>

      {children}
      <p className={styles.note}>{t('pitch_note')}</p>
    </div>
  );
};
